import { useEffect, useMemo, useState } from 'react'
import { BarChart3, X } from 'lucide-react'
import { getExerciseStatsForTeacher } from '../services/attemptService'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './ExerciseStatsModal.css'

/** Nombre max d’erreurs affichées (évite les listes interminables). */
const FREQUENT_ERRORS_MAX = 8
/**
 * Filtre anti-bruit : on masque les lignes très rares sur de gros volumes.
 * Si tout serait masqué, on retombe sur le top sans ce filtre (voir useMemo).
 */
const FREQUENT_ERRORS_MIN_PERCENT = 5

function formatDateTime(value) {
  if (!value) return 'Date inconnue'
  const date = value?.toDate ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return 'Date inconnue'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function getExerciseLabel(exercise) {
  const title = exercise?.metadata?.workTitle?.trim()
  const subtitle = exercise?.metadata?.exerciseTitle?.trim()
  return title || subtitle || exercise?.exerciseTitle || 'Exercice'
}

function ExerciseStatsModal({ isOpen, onClose, exercise, teacherId }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [stats, setStats] = useState(null)
  const [hoveredBucket, setHoveredBucket] = useState(null)
  const backdropDismiss = useBackdropDismiss(onClose)

  useEffect(() => {
    if (!isOpen || !exercise?.id || !teacherId) return undefined
    let cancelled = false
    const load = async () => {
      try {
        const result = await getExerciseStatsForTeacher(teacherId, exercise.id, {
          recentStudentsLimit: 20,
          attemptsCap: 300
        })
        if (cancelled) return
        setStats(result)
        setError('')
      } catch (err) {
        if (cancelled) return
        setError(err?.message || 'Impossible de charger les statistiques.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, exercise?.id, teacherId])

  const scoreBars = useMemo(() => {
    const dist = stats?.scoreDistribution
    const total = stats?.totalAttempts || 1
    if (!dist) return []
    return [
      { label: '0-39', min: 0, max: 39, count: dist.low || 0 },
      { label: '40-69', min: 40, max: 69, count: dist.mid || 0 },
      { label: '70-84', min: 70, max: 84, count: dist.good || 0 },
      { label: '85-100', min: 85, max: 100, count: dist.excellent || 0 }
    ].map((row) => ({
      ...row,
      percent: Math.round((row.count / total) * 100)
    }))
  }, [stats])

  const groupedTopErrors = useMemo(() => {
    const rows = stats?.topErrors || []
    const total = Math.max(1, stats?.totalAttempts || 0)
    if (rows.length === 0) return []
    // Regroupe par (réponse erronée + réponse attendue) pour afficher « X au lieu de Y ».
    const grouped = new Map()
    rows.forEach((row) => {
      const rawWrong = typeof row.label === 'string' ? row.label.trim() : ''
      const wrongLabel = rawWrong || 'Non répondu'
      const rawExpected = typeof row.correctLabel === 'string' ? row.correctLabel.trim() : ''
      const expectedLabel = rawExpected || 'Non répondu'
      const key = `${wrongLabel}\0${expectedLabel}`
      const current = grouped.get(key) || {
        wrongLabel,
        expectedLabel,
        count: 0,
        questions: new Set()
      }
      current.count += Number(row.count) || 0
      if (row.questionIndex != null) current.questions.add(row.questionIndex)
      grouped.set(key, current)
    })
    const mapped = Array.from(grouped.values())
      .map((row) => {
        const percent = Math.round((row.count / total) * 100)
        const same =
          row.wrongLabel === row.expectedLabel ||
          row.expectedLabel === 'Non répondu'
        const title = same
          ? row.wrongLabel
          : `${row.wrongLabel} au lieu de ${row.expectedLabel}`
        return {
          key: `${row.wrongLabel}-${row.expectedLabel}`,
          title,
          wrongLabel: row.wrongLabel,
          expectedLabel: row.expectedLabel,
          count: row.count,
          percent,
          questionsLabel: Array.from(row.questions).sort((a, b) => a - b).join(', ')
        }
      })
      .sort((a, b) => b.count - a.count)

    const passesThreshold = (r) =>
      r.percent >= FREQUENT_ERRORS_MIN_PERCENT || r.count >= 2
    const filtered = mapped.filter(passesThreshold)
    const chosen =
      filtered.length > 0 ? filtered.slice(0, FREQUENT_ERRORS_MAX) : mapped.slice(0, FREQUENT_ERRORS_MAX)

    return chosen
  }, [stats])

  if (!isOpen || !exercise) return null

  return (
    <div className="exercise-stats-overlay" {...backdropDismiss} role="presentation">
      <div
        className="exercise-stats-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exercise-stats-title"
      >
        <button type="button" className="exercise-stats-close" onClick={onClose} aria-label="Fermer">
          <X size={20} />
        </button>

        <div className="exercise-stats-header">
          <div className="exercise-stats-title-row">
            <BarChart3 size={18} />
            <h3 id="exercise-stats-title">Statistiques d&apos;exercice</h3>
          </div>
          <p className="exercise-stats-subtitle">{getExerciseLabel(exercise)}</p>
        </div>

        {loading && (
          <div className="exercise-stats-loading">
            <div className="exercise-stats-spinner" />
            <p>Chargement des statistiques...</p>
          </div>
        )}

        {!loading && error && (
          <div className="exercise-stats-error" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && stats && (
          <div className="exercise-stats-content">
            <div className="exercise-stats-kpis">
              <div className="exercise-stats-kpi">
                <span className="exercise-stats-kpi-label">Tentatives</span>
                <strong>{stats.totalAttempts}</strong>
              </div>
              <div className="exercise-stats-kpi">
                <span className="exercise-stats-kpi-label">Élèves uniques</span>
                <strong>{stats.uniqueStudents}</strong>
              </div>
              <div className="exercise-stats-kpi">
                <span className="exercise-stats-kpi-label">Réussite (≥ {stats.successThreshold}%)</span>
                <strong>{stats.successRate}%</strong>
              </div>
              <div className="exercise-stats-kpi">
                <span className="exercise-stats-kpi-label">Moyenne / Médiane</span>
                <strong>{stats.averageScore}% / {stats.medianScore}%</strong>
              </div>
            </div>

            <p className="exercise-stats-sample-note">
              Analyse basée sur les {stats.sampledAttempts} dernières tentatives (max {stats.attemptsCap}).
            </p>

            <section className="exercise-stats-section">
              <h4>Distribution des scores</h4>
              <div className="exercise-stats-chart" role="img" aria-label="Histogramme de distribution des scores">
                {scoreBars.map((row, idx) => (
                  <button
                    key={row.label}
                    type="button"
                    className={`exercise-stats-chart-col ${hoveredBucket === idx ? 'is-active' : ''}`}
                    onMouseEnter={() => setHoveredBucket(idx)}
                    onMouseLeave={() => setHoveredBucket(null)}
                    onFocus={() => setHoveredBucket(idx)}
                    onBlur={() => setHoveredBucket(null)}
                    title={`${row.label}% : ${row.count} tentative(s), ${row.percent}%`}
                  >
                    <span className="exercise-stats-chart-bar-wrap">
                      <span className="exercise-stats-chart-bar" style={{ height: `${Math.max(10, row.percent)}%` }} />
                    </span>
                    <span className="exercise-stats-chart-label">{row.label}</span>
                  </button>
                ))}
              </div>
              {hoveredBucket != null && scoreBars[hoveredBucket] && (
                <p className="exercise-stats-chart-tooltip" role="status" aria-live="polite">
                  Score {scoreBars[hoveredBucket].label}% : {scoreBars[hoveredBucket].count} tentatives ({scoreBars[hoveredBucket].percent}%)
                </p>
              )}
            </section>

            <section className="exercise-stats-section">
              <h4>Erreurs fréquentes (réponse donnée vs attendue)</h4>
              <p className="exercise-stats-errors-hint">
                Jusqu&apos;à {FREQUENT_ERRORS_MAX} cas les plus fréquents. Une ligne est masquée seulement si elle
                fait moins de {FREQUENT_ERRORS_MIN_PERCENT}% des tentatives <em>et</em> moins de 2 occurrences ; si
                tout serait masqué, le top s&apos;affiche quand même. Le % est la part du nombre de tentatives
                analysées.
              </p>
              {groupedTopErrors.length === 0 ? (
                <p className="exercise-stats-empty">Aucune erreur fréquente détectée.</p>
              ) : (
                <ul className="exercise-stats-errors">
                  {groupedTopErrors.map((row) => (
                    <li key={row.key}>
                      <div>
                        <strong>{row.title}</strong>
                        {row.questionsLabel ? (
                          <p>Accords concernés : {row.questionsLabel}</p>
                        ) : null}
                      </div>
                      <span>
                        {row.count} × ({row.percent}%)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="exercise-stats-section">
              <h4>Derniers 20 élèves actifs</h4>
              {stats.recentStudents.length === 0 ? (
                <p className="exercise-stats-empty">Aucun élève actif sur cet exercice.</p>
              ) : (
                <div className="exercise-stats-table-wrap">
                  <table className="exercise-stats-table">
                    <thead>
                      <tr>
                        <th>Élève</th>
                        <th>Dernière tentative</th>
                        <th>Dernier score</th>
                        <th>Moyenne</th>
                        <th>Tentatives</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentStudents.map((student) => (
                        <tr key={student.userId}>
                          <td>{student.displayName}</td>
                          <td>{formatDateTime(student.lastAttemptAt)}</td>
                          <td>{student.lastScore}%</td>
                          <td>{student.averageScore}%</td>
                          <td>{student.attemptCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExerciseStatsModal
